/**
 * Поиск по мере набора в закрытом справочнике.
 *
 * Отличие от `Select` не в размере списка, а в вопросе. `Select` показывает
 * ВСЕ варианты и годится там, где человек их читает и сравнивает: четыре
 * области видимости, три исхода проверки. `ComboBox` нужен там, где вариантов
 * столько, что читать их бессмысленно, и человек знает имя наизусть: 805
 * хозяйств кампании, 4 149 контуров.
 *
 * Что компонент НЕ делает намеренно:
 *
 * * **не ранжирует.** Порядок — тот, что отдал сервер. Собственная сортировка
 *   «по похожести» была бы оценкой похожести, которой в корпусе нет, и первая
 *   строка читалась бы как «самая подходящая»;
 * * **не фильтрует на клиенте.** `items` контролируемые: фильтрация — работа
 *   того, кто владеет запросом, и подмешивать к ней вторую, клиентскую,
 *   значит показывать выборку, которой нет ни на одной стороне;
 * * **не молчит об обрезанной выдаче.** `footer` существует ради одной фразы:
 *   «ещё N совпадений». Короткий список без неё читается как «больше ничего
 *   нет», хотя выдача просто обрезана.
 *
 * Клавиатура, виртуальный фокус (`aria-activedescendant`), Escape и чтение
 * вслух принадлежат React Aria. Своей клавиатуры здесь нет.
 */

import { Search } from "lucide-react";
import type { ReactNode } from "react";
import {
  ComboBox as AriaComboBox,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Text,
} from "react-aria-components";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { Explained, FIELD_TEXT } from "./InfoPopover";
import styles from "./controls.module.css";

const m = defineMessages({
  loading: { id: "common.kit.combo.loading", defaultMessage: "ищем…", description: "Строка в выпадающем списке поиска, пока идёт запрос; коротко" },
});

/** Строка коллекции: ключ отделён от самой единицы, чтобы её не трогать. */
interface Row<T> {
  id: string;
  item: T;
}

export interface ComboBoxProps<T> {
  /** Видимая подпись. Без неё обязателен `aria-label`. */
  label?: ReactNode;
  "aria-label"?: string;
  /** Объяснение подписи — в том числе правило поиска на сервере. */
  explain?: ReactNode;
  className?: string;
  size?: "sm" | "md";
  placeholder?: string;
  /** Строка под полем: чем сервер ищет и чего он искать не умеет. */
  description?: ReactNode;
  /** Набранная строка. Контролируемая: её владелец — запрос. */
  inputValue: string;
  onInputChange: (value: string) => void;
  /** Уже отфильтрованная сервером выдача, в порядке ответа. */
  items: readonly T[];
  itemKey: (item: T) => string;
  /** Текст строки. Он же — то, что попадёт в поле после выбора. */
  itemLabel: (item: T) => string;
  /** Фраза для чтения вслух целиком, если из кусков она собирается неверно. */
  itemAriaLabel?: (item: T) => string;
  /** Подпись справа: где это. */
  itemHint?: (item: T) => string | null;
  /** Приставка слева: что это за единица. */
  itemPrefix?: (item: T) => string | null;
  /** Выбранный ключ или `null`, если выбора нет. */
  selectedKey?: string | null;
  /** `null` приходит, когда человек очистил поле. */
  onSelect: (item: T | null) => void;
  /**
   * Ставить ли имя выбранного в поле.
   *
   * По умолчанию — да, и это не удобство: поле, оставшееся с обрывком запроса
   * («ор» при выбранном «Саркор»), НАЗЫВАЕТ применённый фильтр неверно, а
   * человек читает по нему выборку. Выключается только там, где строка живёт
   * своей жизнью — например, в адресной книге шапки, где после перехода
   * запрос остаётся запросом.
   */
  commitsInputOnSelect?: boolean;
  /**
   * Что показать, когда выдача пуста и запрос уже отработал. Пока не задано,
   * пустой список не открывается вовсе: пустая рамка молчит хуже, чем
   * закрытый список.
   */
  emptyState?: ReactNode;
  /**
   * Запрос в пути.
   *
   * Это не украшение и не спиннер ради спиннера. React Aria открывает список
   * в ТОТ момент, когда изменилась строка, и только если ему есть что
   * показать. Выдача, приехавшая через 250 мс, список уже не откроет: строка
   * с тех пор не менялась. Поэтому «ищем…» — не сообщение о задержке, а то,
   * ЧТО показывает список, пока ответа нет; без него асинхронный поиск не
   * открывается вовсе.
   */
  isLoading?: boolean;
  /** Что стоит в списке, пока ответа нет. */
  loadingState?: ReactNode;
  /** Строка под списком: «ищем…», «ещё N совпадений», «поиск не ответил». */
  footer?: ReactNode;
  isDisabled?: boolean;
}

export function ComboBox<T>({
  label,
  "aria-label": ariaLabel,
  explain,
  className,
  size = "md",
  placeholder,
  description,
  inputValue,
  onInputChange,
  items,
  itemKey,
  itemLabel,
  itemAriaLabel,
  itemHint,
  itemPrefix,
  selectedKey = null,
  onSelect,
  commitsInputOnSelect = true,
  emptyState,
  isLoading = false,
  loadingState,
  footer,
  isDisabled,
}: ComboBoxProps<T>) {
  const t = useT();
  const rows: Row<T>[] = items.map((item) => ({ id: itemKey(item), item }));

  return (
    <AriaComboBox
      className={`${styles.field} ${size === "sm" ? styles.sizeSm : ""} ${className ?? ""}`}
      items={rows}
      inputValue={inputValue}
      onInputChange={onInputChange}
      selectedKey={selectedKey}
      isDisabled={isDisabled}
      // Набранное не откатывается к выбранному: строка — это запрос человека,
      // и подменять её на имя прошлого выбора значит терять его работу.
      allowsCustomValue
      allowsEmptyCollection={emptyState != null || isLoading}
      aria-label={label ? undefined : ariaLabel}
      onSelectionChange={(key) => {
        if (key == null) {
          onSelect(null);
          return;
        }
        const row = rows.find((candidate) => candidate.id === String(key));
        if (!row) return;
        // Оба значения контролируемые, и React Aria намеренно не трогает
        // строку сама: обновить её — обязанность владельца.
        if (commitsInputOnSelect) onInputChange(itemLabel(row.item));
        onSelect(row.item);
      }}
    >
      {label ? (
        <Label className={styles.label}>
          <Explained
            label={typeof label === "string" ? label : (ariaLabel ?? t(FIELD_TEXT.valueFallback))}
            explain={explain}
          >
            {label}
          </Explained>
        </Label>
      ) : null}

      <div className={styles.inputShell}>
        <Search className={styles.leadIcon} size={16} strokeWidth={1.75} aria-hidden />
        <Input className={styles.input} placeholder={placeholder} />
      </div>

      {description ? (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      ) : null}

      <Popover className={styles.popover}>
        {/* Коллекция берётся из `items` самого ComboBox: только так React Aria
            знает порядок строк для стрелок и Home/End. */}
        <ListBox<Row<T>>
          className={styles.listbox}
          renderEmptyState={
            emptyState != null || isLoading
              ? () => (
                  <div className={styles.note}>
                    {isLoading ? (loadingState ?? t(m.loading)) : emptyState}
                  </div>
                )
              : undefined
          }
        >
          {(row) => (
            <ListBoxItem
              id={row.id}
              className={styles.option}
              textValue={itemLabel(row.item)}
              aria-label={itemAriaLabel?.(row.item)}
            >
              {itemPrefix?.(row.item) ? (
                <span className={styles.optionPrefix}>{itemPrefix(row.item)}</span>
              ) : null}
              <span className={styles.optionLabel}>{itemLabel(row.item)}</span>
              {itemHint?.(row.item) ? (
                <span className={styles.optionHint}>{itemHint(row.item)}</span>
              ) : null}
            </ListBoxItem>
          )}
        </ListBox>
        {footer ? <div className={styles.note}>{footer}</div> : null}
      </Popover>
    </AriaComboBox>
  );
}
